<script lang="ts">
  import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
  import { Camera as CameraIcon, Loader2, Check, X, ChevronLeft, Image, Save, Edit3 } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import Button from './ui/button.svelte';
  import Card from './ui/card.svelte';
  import { flashcardActions, selectedTargetLanguage, categories, filteredCategories } from '../stores/flashcards';
  import { supabase } from '../lib/supabase';
  import { resolveApiUrl } from '../lib/utils';

  let { onBack }: { onBack: () => void } = $props();

  // State
  let isProcessing = $state(false);
  let error = $state<string | null>(null);
  let capturedImage = $state<string | null>(null);
  let extractedText = $state<string>('');
  let isProcessMode = $state(false);
  let markedWords = $state<Set<string>>(new Set());

  // Modal state
  let showWordModal = $state(false);
  let selectedWord = $state('');
  let translation = $state('');
  let isTranslating = $state(false);
  let selectedCategoryId = $state<string | null>(null);

  // Check if user is authenticated
  let isAuthenticated = $state(false);

  $effect(() => {
    checkAuth();
  });

  async function checkAuth() {
    const { data: { user } } = await supabase.auth.getUser();
    isAuthenticated = !!user;
  }

  async function captureImage(source: CameraSource) {
    try {
      error = null;

      if (!isAuthenticated) {
        error = $_('photo_capture.auth_required');
        return;
      }

      // Take photo or pick from gallery
      const image = await Camera.getPhoto({
        quality: 60,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: source,
        width: 1200,
      });

      if (!image.base64String) {
        throw new Error('Failed to capture image');
      }

      capturedImage = `data:image/${image.format};base64,${image.base64String}`;

      // Process the image
      await processImage(capturedImage);
    } catch (err) {
      console.error('Error capturing image:', err);
      const errorMessage = source === CameraSource.Camera
        ? $_('photo_capture.camera_error')
        : $_('photo_capture.gallery_error');
      error = err instanceof Error ? err.message : errorMessage;
    }
  }

  async function takePhoto() {
    await captureImage(CameraSource.Camera);
  }

  async function pickFromGallery() {
    await captureImage(CameraSource.Photos);
  }

  async function processImage(imageData: string) {
    try {
      isProcessing = true;
      error = null;

      const apiUrl = resolveApiUrl('/api/extract-text-from-image');
      console.log('📤 Sending image to API:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ image: imageData }),
      });

      console.log('📥 API response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Nepodařilo se zpracovat obrázek';
        try {
          const errorData = await response.json();
          console.error('API error data:', errorData);

          if (errorData.details) {
            if (errorData.details.includes('GOOGLE_CLOUD_CREDENTIALS')) {
              errorMessage = 'Chyba konfigurace: Chybí Google Cloud credentials pro OCR';
            } else {
              errorMessage = `Chyba: ${errorData.details}`;
            }
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (jsonError) {
          console.error('Failed to parse error response:', jsonError);
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('✅ API response data:', data);

      if (data.success && data.text && data.text.length > 0) {
        extractedText = data.text;
      } else {
        error = 'Z obrázku nebyl rozpoznán žádný text. Zkuste lépe osvětlený nebo ostřejší obrázek.';
      }
    } catch (err) {
      console.error('Error processing image:', err);
      error = err instanceof Error ? err.message : 'Nepodařilo se zpracovat obrázek';
    } finally {
      isProcessing = false;
    }
  }

  function handleWordClick(event: MouseEvent) {
    if (!isProcessMode || !extractedText) return;

    const target = event.target as HTMLElement;
    if (target.classList.contains('word')) {
      const word = target.textContent?.trim() || '';
      if (word) {
        openWordModal(word);
      }
    }
  }

  async function openWordModal(word: string) {
    selectedWord = word;
    translation = '';
    selectedCategoryId = null;
    showWordModal = true;

    // Load categories if not loaded
    await flashcardActions.loadData();

    // Auto-translate using DeepL/Google Translate API
    autoTranslate(word);
  }

  async function autoTranslate(word: string) {
    if (!$selectedTargetLanguage) return;

    isTranslating = true;
    try {
      const response = await fetch(resolveApiUrl('/api/translate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: word,
          sourceLanguage: $selectedTargetLanguage.code,
          targetLanguage: 'cs',
        }),
      });

      const data = await response.json();

      if (data.translations && data.translations.length > 0) {
        translation = data.translations[0];
      }
    } catch (err) {
      console.error('Translation error:', err);
    } finally {
      isTranslating = false;
    }
  }

  async function saveWord() {
    if (!selectedWord || !$selectedTargetLanguage) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Use selected category or create new one
      let categoryId: string | null = selectedCategoryId;

      if (!categoryId) {
        // Create a new category "OCR Import"
        try {
          const { data: newCategory, error: categoryError } = await supabase
            .from('flashcard_categories')
            .insert({
              user_id: user.id,
              name: 'OCR Import',
              target_language_id: $selectedTargetLanguage.id,
              description: 'Slovíčka z OCR importu obrázků',
            })
            .select()
            .maybeSingle();

          if (!categoryError && newCategory) {
            categoryId = newCategory.id;
          }
        } catch (err) {
          console.warn('Error creating category:', err);
        }
      }

      // Create flashcard
      const { data: flashcard, error: flashcardError } = await supabase
        .from('flashcards')
        .insert({
          user_id: user.id,
          primary_text: translation || '',     // Czech (primary language)
          target_text: selectedWord,            // Foreign language (clicked word)
          target_language_id: $selectedTargetLanguage.id,
          category_id: categoryId,
        })
        .select()
        .single();

      if (flashcardError || !flashcard) {
        console.error('Error creating flashcard:', flashcardError);
        return;
      }

      // Update local state immediately
      markedWords.add(selectedWord.toLowerCase());

      showWordModal = false;
      selectedWord = '';
      translation = '';
      selectedCategoryId = null;

      // Reload categories to include newly created one
      await flashcardActions.loadData();
    } catch (err) {
      console.error('Error saving word:', err);
    }
  }

  function renderContent(): string {
    if (!extractedText) return '';

    // Split content into words and wrap each in a span
    return extractedText
      .split(/\s+/)
      .map((word) => {
        const cleanWord = word.toLowerCase().replace(/[.,!?;:]/g, '');
        const isMarked = markedWords.has(cleanWord);
        const className = isProcessMode
          ? `word cursor-pointer hover:bg-muted rounded px-1 ${isMarked ? 'bg-primary/20 text-primary' : ''}`
          : '';
        return `<span class="${className}">${word}</span>`;
      })
      .join(' ');
  }

  function reset() {
    capturedImage = null;
    extractedText = '';
    error = null;
    isProcessMode = false;
    markedWords.clear();
  }
</script>

<div class="min-h-screen bg-background pb-20">
  <div class="container mx-auto px-4 py-6 max-w-md">
    <!-- Header -->
    <div class="mb-6">
      <div class="mb-4">
        <button type="button" class="btn btn-back" onclick={() => onBack()} aria-label={$_('buttons.back')}>
          <ChevronLeft  class="w-4 h-4" />
        </button>
      </div>

      <h1 class="text-2xl font-bold mb-2">{$_('photo_capture.title')}</h1>
      <p class="text-foreground/70">
        Vyfotografujte text a přidávejte slovíčka kliknutím
      </p>
    </div>

    <!-- Error message -->
    {#if error}
      <Card class="p-4 mb-6 bg-destructive/10 border border-destructive/20">
        <div class="flex items-start gap-3">
          <X class="w-5 h-5 text-destructive mt-0.5" />
          <div class="flex-1">
            <p class="text-destructive text-sm">{error}</p>
          </div>
        </div>
      </Card>
    {/if}

    {#if !capturedImage}
      <!-- Initial state - Take photo or pick from gallery -->
      <div class="flex justify-center gap-4 mb-6 mt-8">
        <Card class="widget-action vertical tone-primary w-28">
          <Button onclick={() => takePhoto()} variant="elevated" class="group" disabled={!isAuthenticated}>
            <div class="widget-action-icon">
              <CameraIcon class="w-10 h-10" />
            </div>
            <span class="text-sm font-semibold mb-3">{$_('photo_capture.take_photo')}</span>
          </Button>
        </Card>

        <Card class="widget-action vertical tone-secondary w-28">
          <Button onclick={() => pickFromGallery()} variant="elevated" class="group" disabled={!isAuthenticated}>
            <div class="widget-action-icon">
              <Image class="w-10 h-10" />
            </div>
            <span class="text-sm font-semibold mb-3">{$_('photo_capture.from_gallery')}</span>
          </Button>
        </Card>
      </div>

      {#if !isAuthenticated}
      <Card class="p-8 text-center mb-6">
        <p class="text-sm text-muted-foreground mt-4">
          {$_('photo_capture.login_required')}
        </p>
      </Card>
      {/if}

      <!-- Instructions -->
      <Card class="widget-card tone-header p-6">
        <h3 class="font-semibold mb-3">{$_('photo_capture.tips_title')}</h3>
        <ul class="space-y-2 text-sm text-muted-foreground">
          <li class="flex items-start gap-2">
            <Check class="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>{$_('photo_capture.tip_1')}</span>
          </li>
          <li class="flex items-start gap-2">
            <Check class="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>{$_('photo_capture.tip_2')}</span>
          </li>
          <li class="flex items-start gap-2">
            <Check class="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>{$_('photo_capture.tip_3')}</span>
          </li>
          <li class="flex items-start gap-2">
            <Check class="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>{$_('photo_capture.tip_4')}</span>
          </li>
          <li class="flex items-start gap-2">
            <Check class="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>{$_('photo_capture.tip_5')}</span>
          </li>
        </ul>
      </Card>
    {:else if isProcessing}
      <!-- Processing state -->
      <Card class="p-8 text-center">
        <Loader2 class="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
        <h2 class="section-title mb-2">{$_('photo_capture.processing')}</h2>
        <p class="text-muted-foreground text-sm">
          {$_('photo_capture.processing_details')}
        </p>
      </Card>
    {:else if extractedText.length > 0}
      <!-- Results state - Clickable text like in articles -->
      <Card class="p-4 mb-4">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-2">
            <!-- Process Mode -->
            <Button
              onclick={() => isProcessMode = !isProcessMode}
              variant={isProcessMode ? 'default' : 'outline'}
              class="rounded-lg px-4 py-2"
            >
              <Edit3 class="h-4 w-4 mr-2" />
              {isProcessMode ? $_('photo_capture.add_mode_on') : $_('photo_capture.add_mode_off')}
            </Button>
          </div>
          <button
            onclick={reset}
            class="btn btn-ghost btn-sm h-10 w-10 p-0"
          >
            <X class="h-4 w-4" />
          </button>
        </div>
      </Card>

      <!-- Text Content -->
      <Card class="p-8 mb-4">
        <h2 class="text-xl font-bold mb-4">{$_('photo_capture.recognized_text')}</h2>
        <div
          class="text-base leading-relaxed whitespace-pre-wrap"
          onclick={handleWordClick}
          onkeydown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              const target = e.target as HTMLElement;
              if (target.classList.contains('word')) {
                const word = target.textContent?.trim() || '';
                if (word) {
                  openWordModal(word);
                }
              }
            }
          }}
          role="button"
          tabindex={isProcessMode ? 0 : -1}
        >
          {@html renderContent()}
        </div>
      </Card>
    {/if}
  </div>
</div>

<!-- Word Modal -->
{#if showWordModal}
  <div class="fixed inset-0 z-[50] flex items-center justify-center bg-black/50 p-4">
    <div class="w-full max-w-md p-6 space-y-4 relative z-[96] rounded-2xl border border-border shadow-lg"
         style="background-color: hsl(var(--card));">

      <div class="flex items-center justify-between">
        <h3 class="text-lg font-semibold">
          {$_('photo_capture.add_word_modal_title')}
        </h3>
        <button
          onclick={() => showWordModal = false}
          class="btn btn-close"
        >
          <X class="h-5 w-5" />
        </button>
      </div>

      <div>
        <label for="word-display" class="block text-sm font-medium mb-2">
          {$_('photo_capture.target_language_label')}
        </label>
        <div id="word-display" class="text-2xl font-bold text-primary mb-4">
          {selectedWord}
        </div>
      </div>

      <div>
        <label for="translation-input" class="block text-sm font-medium mb-2">
          {$_('photo_capture.translation_label')}
        </label>
        <div class="relative">
          <input
            id="translation-input"
            type="text"
            bind:value={translation}
            placeholder={$_('photo_capture.translation_placeholder')}
            disabled={isTranslating}
            class="form-input w-full disabled:opacity-50"
          />
          {#if isTranslating}
            <div class="absolute right-3 top-1/2 -translate-y-1/2">
              <div class="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          {/if}
        </div>
      </div>

      <div>
        <label for="category-select" class="block text-sm font-medium mb-2">
          {$_('category_label')}
        </label>
        <select
          id="category-select"
          bind:value={selectedCategoryId}
          class="form-input w-full"
        >
          <option value={null}>{$_('photo_capture.create_new_category')}</option>
          {#each $filteredCategories as category}
            <option value={category.id}>{category.name}</option>
          {/each}
        </select>
      </div>

      <div class="flex gap-3 pt-4">
        <Button
        onclick={() => showWordModal = false}
        variant="outline"
        class="flex-1"
        >
        {$_('photo_capture.cancel_button')}
        </Button>
        <Button
        onclick={saveWord}
        variant="default"
        class="flex-1"
        >
        <Save class="h-4 w-4 mr-2" />
        Uložit
        </Button>
      </div>
    </div>
  </div>
{/if}
